/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/StatusBadge.tsx
 * PURPOSE: CRM semantic status chip — token-based colours for task, activity, opportunity, and priority strings
 * DEPENDENCIES: react, ../../types/activity.types, ../../types/opportunity.types, ../../types/task.types
 * PUBLIC_API: StatusBadge, StatusBadgeProps, CrmStatusValue
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~120/200
 */

import type { CSSProperties } from 'react';
import type { ActivityStatus } from '../../types/activity.types';
import type { OpportunityStage } from '../../types/opportunity.types';
import { TASK_STATUS_LABELS, type TaskStatus } from '../../types/task.types';

export type CrmStatusValue = ActivityStatus | TaskStatus | OpportunityStage | string;

export type StatusBadgeProps = {
  status: CrmStatusValue;
  size?: 'sm' | 'md';
};

type BadgeVariant = 'neutral' | 'info' | 'success' | 'error' | 'warning';

function resolveVariant(status: string): BadgeVariant {
  const s = String(status).toLowerCase();
  if (s === 'open' || s === 'todo') return 'neutral';
  if (s === 'in_progress' || s === 'scheduled') return 'info';
  if (s === 'complete' || s === 'done' || s === 'closed_won') return 'success';
  if (s === 'overdue' || s === 'closed_lost' || s === 'blocked') return 'error';
  if (s === 'high' || s === 'urgent') return 'warning';
  if (s === 'archived') return 'warning';
  return 'neutral';
}

/** Human-readable label from snake_case or raw status (activities, deals — not task slugs). */
function formatStatusLabel(status: string): string {
  const t = String(status).trim();
  if (!t) return '';
  return t
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function resolveStatusBadgeLabel(statusKey: string): string {
  const k = statusKey.trim().toLowerCase();
  if (k in TASK_STATUS_LABELS) return TASK_STATUS_LABELS[k as TaskStatus];
  return formatStatusLabel(statusKey);
}

/** Maps semantic variant to borders and fills using :root design tokens (see index.css). */
const VARIANT_STYLE: Record<BadgeVariant, CSSProperties> = {
  neutral: {
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-secondary)',
  },
  info: {
    border: '1px solid color-mix(in srgb, var(--primary-color) 45%, var(--border-color))',
    backgroundColor: 'color-mix(in srgb, var(--primary-color) 12%, var(--bg-primary))',
    color: 'var(--primary-color)',
  },
  success: {
    border: '1px solid color-mix(in srgb, var(--success-color) 45%, var(--border-color))',
    backgroundColor: 'color-mix(in srgb, var(--success-color) 12%, var(--bg-primary))',
    color: 'var(--success-color)',
  },
  error: {
    border: '1px solid color-mix(in srgb, var(--danger-color) 45%, var(--border-color))',
    backgroundColor: 'color-mix(in srgb, var(--danger-color) 12%, var(--bg-primary))',
    color: 'var(--danger-color)',
  },
  warning: {
    border: '1px solid color-mix(in srgb, var(--warning-color) 45%, var(--border-color))',
    backgroundColor: 'color-mix(in srgb, var(--warning-color) 14%, var(--bg-primary))',
    color: 'var(--warning-color)',
  },
};

const SIZE_CLASS: Record<NonNullable<StatusBadgeProps['size']>, string> = {
  sm: 'text-xs font-medium px-2 py-0.5 rounded-md',
  md: 'text-sm font-medium px-2.5 py-1 rounded-md',
};

export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const key = String(status);
  const label = resolveStatusBadgeLabel(key);

  if (key === 'closed_won') {
    return (
      <span
        data-testid="status-badge"
        className={`inline-flex max-w-full items-center truncate border border-emerald-500/50 bg-emerald-50 text-emerald-800 dark:border-emerald-600/50 dark:bg-emerald-950/40 dark:text-emerald-200 ${SIZE_CLASS[size]}`}
      >
        {label}
      </span>
    );
  }
  if (key === 'closed_lost') {
    return (
      <span
        data-testid="status-badge"
        className={`inline-flex max-w-full items-center truncate border border-red-500/45 bg-red-50 text-red-800 dark:border-red-700/50 dark:bg-red-950/35 dark:text-red-200 ${SIZE_CLASS[size]}`}
      >
        {label}
      </span>
    );
  }
  if (key === 'archived') {
    return (
      <span
        data-testid="status-badge"
        className={`inline-flex max-w-full items-center truncate border border-gray-300 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 ${SIZE_CLASS[size]}`}
      >
        {label}
      </span>
    );
  }

  const variant = resolveVariant(key);

  return (
    <span
      data-testid="status-badge"
      className={`inline-flex max-w-full items-center truncate ${SIZE_CLASS[size]}`}
      style={VARIANT_STYLE[variant]}
    >
      {label}
    </span>
  );
}
